'use client'

import React, { useEffect, useState } from 'react'

import Spacer from 'lib/components/Spacer/Spacer'

import usePolicyDocumentApi from '@/api/usePolicyDocumentApi'
import SiteHeaderWrapper from '@/components/SiteHeaderWrapper'
import { brand } from '@/utils/brand'

import ShipplayPrivacyPage from './ShipplayPrivacyPage'

import './privacy-policy.css'

const PrivacyPage: React.FC = () => {
  return brand.key === 'shipplay' ? <ShipplayPrivacyPage /> : <DefaultPrivacyPage />
}

const DefaultPrivacyPage: React.FC = () => {
  const [rawContent, setRawContent] = useState<string>('')
  const [processedContent, setProcessedContent] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')
  const { fetchPolicyDocument } = usePolicyDocumentApi()

  useEffect(() => {
    const loadPolicyDocument = async (): Promise<void> => {
      try {
        setLoading(true)
        const response = await fetchPolicyDocument(brand.key, 'Privacy')
        setRawContent(response.data.content)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load privacy policy')
      } finally {
        setLoading(false)
      }
    }

    loadPolicyDocument()
  }, [fetchPolicyDocument])

  useEffect(() => {
    if (rawContent) {
      const processTableContent = (htmlContent: string): string => {
        const tablePattern = /<Table>[\s\S]*?<\/Table>/g
        return htmlContent.replace(tablePattern, (tableMatch) => {
          // Extract headers
          const headerMatches = tableMatch.match(/<TableHeader>([^<]+)<\/TableHeader>/g)
          const headers =
            headerMatches?.map((header) =>
              header.replace(/<TableHeader>([^<]+)<\/TableHeader>/, '$1').trim(),
            ) || []

          // Extract table rows
          const rowMatches = tableMatch.match(/<TableRow>[\s\S]*?<\/TableRow>/g)
          const rows: string[][] = []

          rowMatches?.forEach((rowMatch) => {
            const cellMatches = rowMatch.match(/<TableCell[^>]*>([^<]+)<\/TableCell>/g)
            if (cellMatches) {
              const cells = cellMatches.map((cell) =>
                cell.replace(/<TableCell[^>]*>([^<]+)<\/TableCell>/, '$1').trim(),
              )
              rows.push(cells)
            }
          })

          if (headers.length === 0 && rows.length === 0) {
            return tableMatch
          }

          let tableHTML = '<div class="policy-table-container">'
          tableHTML += '<table class="policy-table">'

          if (headers.length > 0) {
            tableHTML += '<thead><tr>'
            headers.forEach((header) => {
              tableHTML += `<th>${header}</th>`
            })
            tableHTML += '</tr></thead>'
          }

          if (rows.length > 0) {
            tableHTML += '<tbody>'
            rows.forEach((row) => {
              tableHTML += '<tr>'
              row.forEach((cell) => {
                tableHTML += `<td>${cell}</td>`
              })
              tableHTML += '</tr>'
            })
            tableHTML += '</tbody>'
          }

          tableHTML += '</table></div>'

          return tableHTML
        })
      }

      const processed = processTableContent(rawContent)
      setProcessedContent(processed)
    }
  }, [rawContent])

  if (loading) {
    return (
      <div className="bg-white">
        <SiteHeaderWrapper />
        <div className="mx-auto pt-32">
          <div className="mx-auto max-w-[1056px] px-6">
            <div className="flex items-center justify-center py-12">
              <div className="text-lg">Loading privacy policy...</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white">
        <SiteHeaderWrapper />
        <div className="mx-auto pt-32">
          <div className="mx-auto max-w-[1056px] px-6">
            <div className="flex items-center justify-center py-12">
              <div className="text-lg text-red-600">Error: {error}</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white">
      <SiteHeaderWrapper />
      <div className="mx-auto pt-32 [&_p]:mt-6">
        <div className="mx-auto max-w-[1056px] px-6">
          <h2 className="mb-6 type-display-3 lg:type-headline-3">Privacy Policy</h2>
          <div className="policy-content" dangerouslySetInnerHTML={{ __html: processedContent }} />
        </div>
      </div>
      <Spacer />
    </div>
  )
}

export default PrivacyPage
